"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Aggregate = void 0;
var _registerHandler = require("../../../common/event-emitter/register-handler");
var _stringify = require("../../../common/util/stringify");
var _handle = require("../../../common/event-emitter/handle");
var _info = require("../../../common/config/info");
var _init = require("../../../common/config/init");
var _runtime = require("../../../common/config/runtime");
var _harvestScheduler = require("../../../common/harvest/harvest-scheduler");
var _denyList = require("../../../common/deny-list/deny-list");
var _constants = require("../constants");
var _features = require("../../../loaders/features/features");
var _constants2 = require("../../metrics/constants");
var _aggregateBase = require("../../utils/aggregate-base");
var _gql = require("./gql");
var _nreum = require("../../../common/window/nreum");
var _chunk = _interopRequireDefault(require("./chunk"));
var _eventBuffer = require("../../utils/event-buffer");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/*
 * Copyright 2020 New Relic Corporation. All rights reserved.
 * SPDX-License-Identifier: Apache-2.0
 */

class Aggregate extends _aggregateBase.AggregateBase {
  static featureName = _constants.FEATURE_NAME;
  #agentInfo;
  #agentRuntime;
  #agentInit;
  constructor(agentIdentifier, aggregator) {
    super(agentIdentifier, aggregator, _constants.FEATURE_NAME);
    this.#agentInfo = (0, _info.getInfo)(agentIdentifier);
    this.#agentRuntime = (0, _runtime.getRuntime)(agentIdentifier);
    this.#agentInit = (0, _init.getConfiguration)(agentIdentifier);
    const harvestTimeSeconds = this.#agentInit.ajax.harvestTimeSeconds || 10;
    (0, _denyList.setDenyList)(this.#agentRuntime.denyList);
    this.ajaxEvents = new _eventBuffer.EventBuffer();
    this.spaAjaxEvents = {};
    const classThis = this;

    // --- v Used by old spa feature
    this.ee.on('interactionDone', (interaction, wasSaved) => {
      if (!this.spaAjaxEvents[interaction.id]?.hasData) return;
      if (!wasSaved) {
        // if the ixn was saved, then its ajax reqs are part of the payload whereas if it was discarded, it should still be harvested in the ajax feature itself
        this.ajaxEvents.merge(this.spaAjaxEvents[interaction.id]);
      }
      delete this.spaAjaxEvents[interaction.id];
    });
    // --- ^
    // --- v Used by new soft nav
    (0, _registerHandler.registerHandler)('returnAjax', event => this.ajaxEvents.add(event), this.featureName, this.ee);
    // --- ^
    (0, _registerHandler.registerHandler)('xhr', function () {
      // the EE-drain system not only switches "this" but also passes a new EventContext with info. Should consider platform refactor to another system which passes a mutable context around separately and predictably to avoid problems like this.
      classThis.storeXhr(...arguments, this); // this switches the context back to the class instance while passing the NR context as an argument -- see "ctx" in storeXhr
    }, this.featureName, this.ee);
    this.waitForFlags([]).then(() => {
      const scheduler = new _harvestScheduler.HarvestScheduler('events', {
        onFinished: this.onEventsHarvestFinished.bind(this),
        getPayload: this.prepareHarvest.bind(this)
      }, this);
      scheduler.startTimer(harvestTimeSeconds);
      this.drain();
    });
  }
  storeXhr(params, metrics, startTime, endTime, type, ctx) {
    metrics.time = startTime;

    // send to session traces
    let hash;
    if (params.cat) {
      hash = (0, _stringify.stringify)([params.status, params.cat]);
    } else {
      hash = (0, _stringify.stringify)([params.status, params.host, params.pathname]);
    }
    const shouldCollect = (0, _denyList.shouldCollectEvent)(params);
    const shouldOmitAjaxMetrics = this.#agentInit.feature_flags?.includes('ajax_metrics_deny_list');

    // store for timeslice metric (harvested by jserrors feature)
    if (shouldCollect || !shouldOmitAjaxMetrics) {
      this.aggregator.store('xhr', hash, params, metrics);
    }
    if (!shouldCollect) {
      if (params.hostname === this.#agentInfo.errorBeacon || this.#agentInit.proxy?.beacon && params.hostname === this.#agentInit.proxy.beacon) {
        // This doesn't make a distinction if the same-domain request is going to a different port or path...
        (0, _handle.handle)(_constants2.SUPPORTABILITY_METRIC_CHANNEL, ['Ajax/Events/Excluded/Agent'], undefined, _features.FEATURE_NAMES.metrics, this.ee);
        if (shouldOmitAjaxMetrics) (0, _handle.handle)(_constants2.SUPPORTABILITY_METRIC_CHANNEL, ['Ajax/Metrics/Excluded/Agent'], undefined, _features.FEATURE_NAMES.metrics, this.ee);
      } else {
        (0, _handle.handle)(_constants2.SUPPORTABILITY_METRIC_CHANNEL, ['Ajax/Events/Excluded/App'], undefined, _features.FEATURE_NAMES.metrics, this.ee);
        if (shouldOmitAjaxMetrics) (0, _handle.handle)(_constants2.SUPPORTABILITY_METRIC_CHANNEL, ['Ajax/Metrics/Excluded/App'], undefined, _features.FEATURE_NAMES.metrics, this.ee);
      }
      return; // do not send this ajax as an event
    }
    (0, _handle.handle)('bstXhrAgg', ['xhr', hash, params, metrics], undefined, _features.FEATURE_NAMES.sessionTrace, this.ee); // have trace feature harvest AjaxNode

    const event = {
      method: params.method,
      status: params.status,
      domain: params.host,
      path: params.pathname,
      requestSize: metrics.txSize,
      responseSize: metrics.rxSize,
      type,
      startTime,
      endTime,
      callbackDuration: metrics.cbTime
    };
    if (ctx.dt) {
      event.spanId = ctx.dt.spanId;
      event.traceId = ctx.dt.traceId;
      event.spanTimestamp = Math.floor(this.#agentRuntime.timeKeeper.correctAbsoluteTimestamp(ctx.dt.timestamp));
    }

    // parsed from the AJAX body, looking for operationName param & parsing query for operationType
    event.gql = params.gql = (0, _gql.parseGQL)({
      body: ctx.body,
      query: ctx.parsedOrigin?.search
    });
    if (event.gql) (0, _handle.handle)(_constants2.SUPPORTABILITY_METRIC_CHANNEL, ['Ajax/Events/GraphQL/Bytes-Added', (0, _stringify.stringify)(event.gql).length], undefined, _features.FEATURE_NAMES.metrics, this.ee);
    const softNavInUse = Boolean((0, _nreum.getNREUMInitializedAgent)(this.agentIdentifier)?.features?.[_features.FEATURE_NAMES.softNav]);
    if (softNavInUse) {
      // For newer soft nav (when running), pass the event to it for evaluation -- either part of an interaction or is given back
      (0, _handle.handle)('ajax', [event], undefined, _features.FEATURE_NAMES.softNav, this.ee);
    } else if (ctx.spaNode) {
      // For old spa (when running), if the ajax happened inside an interaction, hold it until the interaction finishes
      const interactionId = ctx.spaNode.interaction.id;
      this.spaAjaxEvents[interactionId] ??= new _eventBuffer.EventBuffer();
      this.spaAjaxEvents[interactionId].add(event);
    } else {
      this.ajaxEvents.add(event);
    }
  }
  prepareHarvest(options) {
    options = options || {};
    if (this.ajaxEvents.buffer.length === 0) return null;
    const payload = this.#getPayload(this.ajaxEvents.buffer);
    const payloadObjs = [];
    for (let i = 0; i < payload.length; i++) payloadObjs.push({
      body: {
        e: payload[i]
      }
    });
    if (options.retry) this.ajaxEvents.hold();else this.ajaxEvents.clear();
    return payloadObjs;
  }
  onEventsHarvestFinished(result) {
    if (result.retry && this.ajaxEvents.held.hasData) this.ajaxEvents.unhold();else this.ajaxEvents.held.clear();
  }
  #getPayload(events, numberOfChunks) {
    numberOfChunks = numberOfChunks || 1;
    const payload = [];
    const chunkSize = events.length / numberOfChunks;
    const eventChunks = splitChunks.call(this, events, chunkSize);
    let tooBig = false;
    for (let i = 0; i < eventChunks.length; i++) {
      const currentChunk = eventChunks[i];
      if (currentChunk.tooBig) {
        if (currentChunk.events.length > 1) {
          tooBig = true;
          break; // if the payload is too big BUT is made of more than 1 event, we can split it down again
        }
        // Otherwise, if it consists of one sole event, we do not send it (discarded) since we cannot break it apart any further.
      } else {
        payload.push(currentChunk.payload);
      }
    }
    // Check if the current payload string is too big, if so then run getPayload again with more buckets.
    return tooBig ? this.#getPayload(events, ++numberOfChunks) : payload;
    function splitChunks(arr, chunkSize) {
      chunkSize = chunkSize || arr.length;
      const chunks = [];
      for (let i = 0, len = arr.length; i < len; i += chunkSize) {
        chunks.push(new _chunk.default(arr.slice(i, i + chunkSize), this));
      }
      return chunks;
    }
  }
}
exports.Aggregate = Aggregate;